import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo from "@/components/base/Seo";
import CategoryFilterBar from "@/pages/category-index/components/CategoryFilterBar";
import LetterGroup from "@/pages/category-index/components/LetterGroup";
import { getCategoryIndex } from "@/services/categoryIndexService";
import { getCategories, getCategoryAliases } from "@/services/directoryService";
import { buildAliasMap, findBrandedCategorySlug } from "@/utils/categoryMatch";
import type { Category, CategoryLetterGroup } from "@/types/directory";

export default function CategoryIndexPage() {
  const [query, setQuery] = useState("");
  const { hash } = useLocation();
  const [groups, setGroups] = useState<CategoryLetterGroup[]>([]);
  const [brandedCategories, setBrandedCategories] = useState<Category[]>([]);
  const [aliasMap, setAliasMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  // The A–Z index is loaded from the backend (detailed_categories table).
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getCategoryIndex()
      .then((list) => {
        if (active) setGroups(list);
      })
      .catch(() => {
        if (active) {
          setGroups([]);
          setError("We could not load the category index right now. Please try again.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [reloadToken]);

  // Deep-link scroll (e.g. /categories-a-z#letter-M) once the groups are rendered.
  useEffect(() => {
    if (!hash || loading) return;
    const target = document.getElementById(hash.replace("#", ""));
    if (target) {
      const timer = window.setTimeout(() => {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [hash, loading, groups]);

  const letters = useMemo(() => groups.map((group) => group.letter), [groups]);

  // Curated "branded" categories (the ones with their own /category/<slug> page).
  // These decide which A–Z names link straight to a branded page.
  useEffect(() => {
    let active = true;
    getCategories()
      .then((list) => {
        if (active) setBrandedCategories(list);
      })
      .catch(() => {
        if (active) setBrandedCategories([]);
      });
    return () => {
      active = false;
    };
  }, []);

  // Admin-managed aliases — explicit A–Z name → branded slug overrides that win
  // over the automatic matching. Failure is non-fatal (falls back to matching).
  useEffect(() => {
    let active = true;
    getCategoryAliases()
      .then((list) => {
        if (active) setAliasMap(buildAliasMap(list));
      })
      .catch(() => {
        if (active) setAliasMap(new Map());
      });
    return () => {
      active = false;
    };
  }, []);

  // A–Z category name → branded slug, only for names that map to a real page.
  const brandedSlugs = useMemo(() => {
    const map = new Map<string, string>();
    if (brandedCategories.length === 0) return map;
    for (const group of groups) {
      for (const category of group.categories) {
        const slug = findBrandedCategorySlug(category.name, brandedCategories, aliasMap);
        if (slug) map.set(category.name, slug);
      }
    }
    return map;
  }, [groups, brandedCategories, aliasMap]);

  const totalCategories = useMemo(
    () => groups.reduce((sum, group) => sum + group.categories.length, 0),
    [groups],
  );

  const normalized = query.trim().toLowerCase();

  const visibleGroups = useMemo(() => {
    if (!normalized) return groups;
    return groups
      .map((group) => ({
        ...group,
        categories: group.categories.filter((category) =>
          category.name.toLowerCase().includes(normalized),
        ),
      }))
      .filter((group) => group.categories.length > 0);
  }, [groups, normalized]);

  const matchCount = useMemo(
    () => visibleGroups.reduce((sum, group) => sum + group.categories.length, 0),
    [visibleGroups],
  );

  return (
    <SiteLayout>
      <Seo
        title="A to Z Business Categories in Coastal Karnataka"
        description="Browse every business category on Coastal Bizz Connect in alphabetical order — from AC dealers and bakeries to schools, tailors, hospitals and hotels across South Canara, Udupi and North Canara."
        keywords="A to Z business categories, category list Karnataka, Mangaluru business types, Udupi directory categories, local business index"
        path="/categories-a-z"
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <Link to="/categories" className="cursor-pointer transition-colors hover:text-primary-700">
              Categories
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">A to Z Categories</span>
          </nav>

          <div className="mt-3 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
                A to Z business categories
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                Every business type on Coastal Bizz Connect, arranged alphabetically. Pick a letter to
                jump straight to that section, then tap any category to see matching listings across
                South Canara, Udupi and North Canara.
              </p>
            </div>

            <div className="flex shrink-0 gap-3">
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
                <p className="font-heading text-xl font-extrabold text-primary-700">{loading ? "—" : letters.length}</p>
                <p className="mt-0.5 text-xs font-medium text-foreground-500">Letter groups</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 px-5 py-3">
                <p className="font-heading text-xl font-extrabold text-accent-600">
                  {loading ? "—" : `${totalCategories.toLocaleString("en-IN")}+`}
                </p>
                <p className="mt-0.5 text-xs font-medium text-foreground-500">Business categories</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CategoryFilterBar
        letters={letters}
        query={query}
        onQueryChange={setQuery}
        matchCount={matchCount}
        totalCategories={totalCategories}
      />

      <section className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-14">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-400" />
          </div>
        ) : error ? (
          <div className="flex items-start justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span className="flex items-start gap-2.5">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
              {error}
            </span>
            <button
              type="button"
              onClick={() => setReloadToken((token) => token + 1)}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              Retry
            </button>
          </div>
        ) : normalized && matchCount === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-search-line text-2xl" />
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              No categories match &ldquo;{query.trim()}&rdquo;
            </h2>
            <p className="mt-1.5 max-w-md text-sm text-foreground-600">
              Try a shorter keyword, check the spelling, or browse the full A to Z list instead.
            </p>
            <button
              type="button"
              onClick={() => setQuery("")}
              className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-refresh-line text-base" />
              </span>
              Show all categories
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-12">
            {visibleGroups.map((group) => (
              <LetterGroup key={group.letter} group={group} brandedSlugs={brandedSlugs} />
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-start gap-5 px-6 py-10 md:flex-row md:items-center md:justify-between md:py-12">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              Can&apos;t find your business category?
            </h2>
            <p className="mt-1.5 max-w-xl text-sm text-foreground-600">
              Add your business and our team will place it in the right category so nearby customers
              can find you faster.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/categories"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors duration-200 hover:bg-background-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-layout-grid-line text-base" />
              </span>
              Browse top categories
            </Link>
            <Link
              to="/list-your-business"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-accent-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-add-line text-base" />
              </span>
              List your business
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}